import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { PortalBadge } from '../arkorbit-ui/PortalBadge';
import { getLexcoreSnapshotInfo } from './lexcoreSnapshot';

export type PortalLexcoreSnapshotBadgeSize = 'xs' | 'sm';

export interface PortalLexcoreSnapshotBadgeProps {
  size?: PortalLexcoreSnapshotBadgeSize;
  /** `false` = nur Kurzlabel (z. B. `0607`) für sehr schmale Leisten. */
  showPrefix?: boolean;
  /** Klick öffnet Popover mit Snapshot-Details (Sidebar-Footer). */
  interactive?: boolean;
  className?: string;
}

const SIZE_CLASS: Record<PortalLexcoreSnapshotBadgeSize, string> = {
  xs: 'portal-snapshot-stamp !font-medium max-w-full truncate',
  sm: 'portal-snapshot-stamp !font-medium max-w-full truncate',
};

/** Persistenter LEXCORE-Rechtsstand — Build-Env, kein Service-Call. */
export const PortalLexcoreSnapshotBadge: React.FC<PortalLexcoreSnapshotBadgeProps> = ({
  size = 'sm',
  showPrefix = true,
  interactive = false,
  className = '',
}) => {
  const info = getLexcoreSnapshotInfo();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => {
    if (!interactive) return;
    setOpen(v => !v);
  }, [interactive]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!info) return null;

  const label = showPrefix ? info.badgeLabel : info.shortLabel;

  const badge = (
    <PortalBadge
      label={label}
      accent="emerald"
      icon={<ShieldCheck className={size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3'} aria-hidden />}
      className={`${SIZE_CLASS[size]} portal-stamp-lex ${interactive ? 'cursor-pointer hover:ring-1 hover:ring-emerald-400/30' : ''} ${className}`}
    />
  );

  if (!interactive) {
    return (
      <span title={`${info.title} (${info.raw})`} className="inline-flex min-w-0 max-w-full">
        {badge}
      </span>
    );
  }

  return (
    <div ref={rootRef} className="relative inline-flex min-w-0 max-w-full">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={popoverId}
        title="Rechtsstand-Details anzeigen"
        className="inline-flex min-w-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
      >
        {badge}
      </button>
      {open ? (
        <div
          id={popoverId}
          role="dialog"
          aria-label="Normenstand"
          className="absolute left-0 bottom-full z-50 mb-1.5 w-[min(16rem,calc(100vw-2rem))] rounded-lg border border-white/[0.08] bg-slate-900/95 shadow-lg backdrop-blur-sm p-2.5 text-left"
        >
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-400/90 mb-1.5">
            Normenstand
          </p>
          <dl className="space-y-1 text-[10px] text-slate-300">
            <div className="flex gap-2 min-w-0">
              <dt className="text-slate-500 flex-shrink-0 w-14">Stand</dt>
              <dd className="font-medium text-slate-200 truncate">{info.badgeLabel}</dd>
            </div>
            {info.snapshotDate ? (
              <div className="flex gap-2 min-w-0">
                <dt className="text-slate-500 flex-shrink-0 w-14">Datum</dt>
                <dd>{info.snapshotDate}</dd>
              </div>
            ) : null}
            <div className="flex gap-2 min-w-0">
              <dt className="text-slate-500 flex-shrink-0 w-14">Paket</dt>
              <dd className="truncate">{info.packageLabel}</dd>
            </div>
            <div className="flex gap-2 min-w-0">
              <dt className="text-slate-500 flex-shrink-0 w-14">Kennung</dt>
              <dd className="font-mono text-[9px] text-slate-400 break-all">{info.raw}</dd>
            </div>
          </dl>
          <p className="mt-2 pt-1.5 border-t border-white/[0.06] text-[9px] text-slate-500 leading-snug">
            Gültig für Rechtsfragen in ARKO. Quelle: kuratierter Normenbestand
            zum Zeitpunkt dieses Portal-Stands.
          </p>
        </div>
      ) : null}
    </div>
  );
};
